import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChartLineUp,
  Users,
  UsersThree,
  Briefcase,
  FileText,
  Translate,
  ChatsCircle,
  CalendarBlank,
  Receipt,
  Sparkle,
  UserCircle,
  SignOut,
  ShieldStar,
  CreditCard,
  Gear,
  ChartBar,
  List as BurgerIcon,
  X,
} from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, tid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, tid: "nav-clients" },
  { to: "/interpreters", label: "Interpreters", icon: UsersThree, tid: "nav-interpreters" },
  { to: "/projects", label: "Projects", icon: Briefcase, tid: "nav-projects" },
  { to: "/translations", label: "Translations", icon: Translate, tid: "nav-translations" },
  { to: "/contracts", label: "Contracts", icon: FileText, tid: "nav-contracts" },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank, tid: "nav-calendar" },
  { to: "/communications", label: "Communications", icon: ChatsCircle, tid: "nav-communications" },
  { to: "/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices" },
  { to: "/payments", label: "Payments", icon: CreditCard, tid: "nav-payments" },
  { to: "/reports", label: "Reports", icon: ChartBar, tid: "nav-reports" },
  { to: "/ai", label: "AI Assistant", icon: Sparkle, tid: "nav-ai" },
];

function SidebarNav({ user, onNavigate, logout, navigate }) {
  return (
    <>
      <div className="px-4 py-5 flex items-center gap-3 border-b border-white/5">
        <div className="w-10 h-10 rounded-md bg-white/95 flex items-center justify-center p-1 shrink-0">
          <img src={LOGO_URL} alt="Unity Language Group" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-white text-sm leading-tight truncate">Unity Language</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-0.5">Group · Ops Console</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
          Workspace
        </div>
        {NAV.map(({ to, label, icon: Icon, tid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={tid}
            onClick={onNavigate}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
          >
            <Icon size={18} weight="duotone" />
            {label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <>
            <div className="px-2 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Admin
            </div>
            <NavLink to="/team" data-testid="nav-team" onClick={onNavigate}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <ShieldStar size={18} weight="duotone" /> Team
            </NavLink>
            <NavLink to="/pending-applications" data-testid="nav-pending" onClick={onNavigate}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <UsersThree size={18} weight="duotone" /> Pending Interpreters
            </NavLink>
            <NavLink to="/pending-clients" data-testid="nav-pending-clients" onClick={onNavigate}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <UsersThree size={18} weight="duotone" /> Pending Clients
            </NavLink>
            <NavLink to="/settings" data-testid="nav-settings" onClick={onNavigate}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
              <Gear size={18} weight="duotone" /> Company Settings
            </NavLink>
          </>
        )}
      </nav>

      <div className="p-3 border-t border-white/5">
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/5 transition-colors" data-testid="user-menu-trigger">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-800 text-white text-xs">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="text-left flex-1 min-w-0">
              <div className="text-sm text-white font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{user?.role}</div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56 bg-white">
            <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { onNavigate?.(); navigate("/dashboard"); }} data-testid="menu-dashboard">
              <UserCircle size={16} className="mr-2" /> My workspace
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => window.open("/privacy", "_blank")} data-testid="menu-privacy">
              Privacy Policy
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.open("/terms", "_blank")} data-testid="menu-terms">
              Terms of Service
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
              <SignOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {/* Desktop sidebar */}
      <aside className="sidebar-dark w-[250px] shrink-0 hidden md:flex flex-col" data-testid="sidebar">
        <SidebarNav user={user} logout={logout} navigate={navigate} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0F172A] text-white flex items-center gap-3 px-4 h-14 border-b border-white/10" data-testid="mobile-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-2 rounded-md hover:bg-white/10 active:bg-white/20"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <BurgerIcon size={22} weight="bold" />
        </button>
        <div className="w-8 h-8 rounded-md bg-white/95 flex items-center justify-center p-1 shrink-0">
          <img src={LOGO_URL} alt="" className="max-h-full max-w-full object-contain" />
        </div>
        <div className="font-display font-bold text-sm truncate">Unity Language Group</div>
      </div>

      {/* Mobile slide-in navigation */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="sidebar-dark p-0 w-[280px] border-none flex flex-col" data-testid="mobile-nav-sheet">
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-4 right-3 z-50 text-white/70 hover:text-white p-1"
            data-testid="mobile-nav-close"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
          <SidebarNav
            user={user}
            logout={() => { setMobileOpen(false); logout(); }}
            navigate={navigate}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="p-4 pt-[76px] md:p-6 md:pt-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
